import { GraduationCap, ShieldCheck, Store } from 'lucide-react'
import { useProPaywallPage, type UseProPaywallPageProps } from './hooks/use-pro-paywall-page'
import { ProBillingToggle } from './components/pro-billing-toggle'
import { ProNotNowState } from './components/pro-not-now-state'
import { ProFeatureList } from './components/pro-feature-list'
import { ProNoticePanel } from './components/pro-notice-panel'
import { ProPlanChoice } from './components/pro-plan-choice'
import { ProPromoField } from './components/pro-promo-field'
import { ProUpgradeConfirmDialog } from './components/pro-upgrade-confirm-dialog'
import { ProUpgradePanel } from './components/pro-upgrade-panel'
import { ProWuBundle } from './components/pro-wu-bundle'

export type ProPaywallPageProps = UseProPaywallPageProps

export function ProPaywallPage(props: ProPaywallPageProps) {
  const vm = useProPaywallPage(props)

  if (!vm.ready) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background"
        data-testid="pro-paywall-loading"
      >
        <span className="text-sm text-muted-foreground">Loading…</span>
      </div>
    )
  }

  if (vm.showNotNowState) {
    return <ProNotNowState inApp={vm.notNowInApp} />
  }

  return (
    <div className="min-h-screen overflow-hidden bg-card" data-testid="pro-paywall-page">
      <div className="relative overflow-hidden">
        <img
          src="/hunter_hero.jpg"
          alt=""
          aria-hidden
          data-testid="pro-paywall-hero"
          className="absolute inset-0 size-full object-cover [filter:brightness(0.5)_saturate(0.8)]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-card" />
        <div className="relative mx-auto flex w-full max-w-xl flex-col gap-2 px-6 pb-5 pt-4">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
            BlindBook Pro
          </span>
          <p className="font-display text-3xl font-extrabold leading-[1.15] text-white">
            Every tool for the season.
          </p>
          <p className="text-sm text-white/80">
            Offline maps, landowner parcels, live radar, and analytics that show what works.
          </p>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-xl flex-col gap-4 px-6 pb-6 pt-1">
        {vm.isPanelLoading ? (
          <p className="text-sm text-muted-foreground" data-testid="pro-panel-loading">
            Checking your subscription…
          </p>
        ) : null}

        {vm.showOrgGrantNotice ? (
          <ProNoticePanel
            testId="pro-org-grant-notice"
            icon={<ShieldCheck className="size-6" />}
            heading="You have Pro through your organization"
            description="Pro features are active while you are in your organization's context. Get BlindBook Pro to use them everywhere, including your personal hunts."
          />
        ) : null}

        {vm.showPlanForm ? (
          <>
            <ProPlanChoice
              selection={vm.planChoice}
              proPriceLabel={vm.proPriceLabel}
              wuPriceLabel={vm.wuPriceLabel}
              onChange={vm.setPlanChoice}
              onShowProFeatures={vm.handleShowProFeatures}
              proBillingSlot={
                vm.planChoice === 'pro' ? (
                  <ProBillingToggle
                    selection={vm.billingPeriod}
                    monthlyPriceLabel={vm.monthlyPriceLabel}
                    annualPriceLabel={vm.annualPriceLabel}
                    annualSavingsLabel={vm.annualSavingsLabel}
                    onChange={vm.setBillingPeriod}
                  />
                ) : null
              }
            />

            <ProPromoField
              value={vm.promoCode}
              applied={vm.promoApplied}
              appliedPromo={vm.appliedPromo}
              isApplying={vm.isApplyingPromo}
              disabled={vm.isRedirecting}
              error={vm.promoError}
              onChange={vm.setPromoCode}
              onApply={() => {
                void vm.handleApplyPromo()
              }}
            />

            {vm.error ? (
              <p role="alert" className="text-sm text-destructive">
                {vm.error}
              </p>
            ) : null}

            <button
              type="button"
              data-testid="pro-go-pro"
              disabled={vm.isRedirecting}
              onClick={() => {
                void vm.handleContinue()
              }}
              className="w-full rounded-lg bg-primary px-4 py-3.5 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
            >
              {vm.ctaLabel}
            </button>
          </>
        ) : null}

        {vm.panel === 'stripe-upgrade' ? (
          <>
            <ProUpgradePanel
              wuPriceLabel={vm.wuPriceLabel}
              upgrade={vm.upgrade}
              onReview={vm.handleReviewUpgrade}
            />
            <ProUpgradeConfirmDialog
              confirm={vm.upgrade.confirm}
              onConfirm={() => {
                void vm.handleConfirmUpgrade()
              }}
              onCancel={() => {
                vm.handleUpgradeDialogOpenChange(false)
              }}
            />
          </>
        ) : null}

        {vm.panel === 'iap-managed' ? (
          <ProNoticePanel
            testId="pro-iap-managed"
            icon={<Store className="size-6" />}
            heading="Add Waterfowl U in the store"
            description={vm.manageInStore.description}
            storeUrl={vm.manageInStore.url}
            storeLinkLabel={vm.manageInStore.linkLabel}
          />
        ) : null}

        {vm.panel === 'bundled' ? (
          <ProNoticePanel
            testId="pro-bundled"
            icon={<GraduationCap className="size-6" />}
            heading="You have everything"
            description="Your subscription already includes Waterfowl U and every BlindBook Pro feature. There is nothing else to buy."
          />
        ) : null}

        {vm.showNotNow ? (
          <div className="text-center">
            <button
              type="button"
              data-testid="pro-not-now"
              disabled={vm.isRedirecting}
              onClick={vm.handleNotNow}
              className="text-sm font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline disabled:pointer-events-none disabled:opacity-50"
            >
              Not now
            </button>
          </div>
        ) : null}

        {vm.showInAppOption ? (
          <div className="text-center">
            <button
              type="button"
              data-testid="pro-in-app"
              onClick={vm.handleInAppPurchase}
              className="text-sm font-medium text-foreground underline-offset-4 transition-colors hover:underline"
            >
              Or use in-app purchase
            </button>
          </div>
        ) : null}

        <div className="mt-4 flex flex-col gap-4">
          {vm.showWuFeatures ? <ProWuBundle /> : null}
          {vm.showProFeatures ? <ProFeatureList /> : null}
        </div>

        {vm.showRestoreOption ? (
          <div className="mt-2 border-t pt-5 text-center">
            <button
              type="button"
              data-testid="pro-restore"
              onClick={vm.handleRestore}
              className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              Restore purchases
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
